"use client";

import type { ReactNode } from "react";
import { Bookmark, Heart } from "lucide-react";

import type { InteractionModel } from "@/features/interactions/models";
import { useLikeToggle } from "@/features/likes/useLikeToggle";
import { useWatchlistToggle } from "@/features/watchlist/useWatchlistToggle";
import "./tile-actions.css";

type Props = {
  model: InteractionModel;
  id: number | string | undefined;
  lang: "fa" | "en";
  like: { initialLiked: boolean; count: number };
  initialSaved: boolean;
  children: ReactNode;
};

/**
 * Like and save on any v2 catalogue tile — the two toggles the legacy cards
 * carried, laid on the media's corner above the tile's stretched link. Both
 * are the app's own hooks, unchanged (optimistic, rolled back on failure,
 * login prompt for a guest). /saved has its own sibling, SavedItem, because
 * unsaving there also marks the tile as removed.
 */
export default function TileActions({ model, id, lang, like, initialSaved, children }: Props) {
  const isFa = lang === "fa";
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const likeToggle = useLikeToggle({ modelType: model, modelId: id, initialLiked: like.initialLiked, initialCount: like.count, errorMessage: isFa ? "خطا در ثبت پسند" : "Failed to update like" });
  const saveToggle = useWatchlistToggle({ modelType: model, modelId: id, initialWatchlisted: initialSaved, errorMessage: isFa ? "خطا در ثبت واچ‌لیست" : "Failed to update watchlist" });
  const { liked, count } = likeToggle;
  const saved = saveToggle.watchlisted;

  return (
    <div className="v2-acted">
      {children}
      <div className="v2-acts">
        <button type="button" className="v2-act v2-focus" onClick={likeToggle.toggle} aria-pressed={liked} aria-busy={likeToggle.isPending || undefined}
          aria-label={isFa ? (liked ? "برداشتن پسند" : "پسندیدن") : liked ? "Unlike" : "Like"}>
          <Heart aria-hidden />
          {count > 0 ? <b>{nf.format(count)}</b> : null}
        </button>
        <button type="button" className="v2-act v2-act--save v2-focus" onClick={saveToggle.toggle} aria-pressed={saved} aria-busy={saveToggle.isPending || undefined}
          aria-label={isFa ? (saved ? "حذف از ذخیره‌ها" : "ذخیره") : saved ? "Remove from saved" : "Save"}>
          <Bookmark aria-hidden />
        </button>
      </div>
    </div>
  );
}
